import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithRedirect } from '../base-query-with-redirect';

export type ImportStatus = 'QUEUED' | 'PROCESSING' | 'SUCCEEDED' | 'PARTIALLY_SUCCEEDED' | 'FAILED';
export type ImportIssueCode = 'FILE_TOO_LARGE' | 'UNSUPPORTED_FILE_TYPE' | 'INVALID_FILENAME'
   | 'PDF_UNREADABLE' | 'PDF_PROCESSING_FAILED' | 'QR_MISSING_OR_UNREADABLE'
   | 'UNSUPPORTED_QR_FORMAT' | 'INVALID_QR_DATA' | 'INVALID_AMOUNT' | 'FILE_STORAGE_FAILED'
   | 'INVOICE_SAVE_FAILED' | 'PROCESSING_FAILED' | 'PROCESSING_INTERRUPTED';
export interface ImportAttempt {
   id: number;
   filename: string;
   source: 'UPLOAD' | 'EMAIL' | 'AUTOMATIC';
   uploadedBy: string | null;
   receivedAt: string;
   completedAt: string | null;
   status: ImportStatus;
   importedCount: number;
   issues: { code: ImportIssueCode; page: number | null }[];
   read: boolean;
}
export interface ImportActivity {
   items: ImportAttempt[];
   nextBeforeId: number | null;
}
export interface ImportSummary {
   unreadCount: number;
   pendingCount: number;
   latestCompletedAt: string | null;
}
export const isImportIssue = (attempt: ImportAttempt) =>
   attempt.status === 'FAILED' || attempt.status === 'PARTIALLY_SUCCEEDED';

export const importAttemptsApi = createApi({
   reducerPath: 'importAttempts/api',
   baseQuery: baseQueryWithRedirect,
   tagTypes: ['Activity', 'Summary'],
   endpoints: build => ({
      getImportSummary: build.query<ImportSummary, void>({
         query: () => 'import-attempts/summary',
         providesTags: ['Summary'],
      }),
      getImportActivity: build.infiniteQuery<ImportActivity, boolean, number | null>({
         infiniteQueryOptions: {
            initialPageParam: null,
            getNextPageParam: lastPage => lastPage.nextBeforeId ?? undefined,
         },
         query: ({ queryArg, pageParam }) => ({
            url: 'import-attempts',
            params: { unreadOnly: queryArg, size: 20, ...(pageParam === null ? {} : { beforeId: pageParam }) },
         }),
         providesTags: ['Activity'],
      }),
      markImportRead: build.mutation<void, number>({
         query: id => ({ url: `import-attempts/${id}/read`, method: 'POST' }),
         invalidatesTags: ['Activity', 'Summary'],
      }),
      markAllImportsRead: build.mutation<void, void>({
         query: () => ({ url: 'import-attempts/read-all', method: 'POST' }),
         invalidatesTags: ['Activity', 'Summary'],
      }),
   }),
});

export const {
   useGetImportSummaryQuery, useGetImportActivityInfiniteQuery,
   useMarkImportReadMutation, useMarkAllImportsReadMutation,
} = importAttemptsApi;
